import React, { useState } from 'react';
import { 
  DataGrid, 
  GridColDef, 
  GridActionsCellItem 
} from '@mui/x-data-grid';
import { 
  Box, 
  Button, 
  CircularProgress, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  FormControl, 
  InputLabel, 
  MenuItem, 
  Select, 
  Stack, 
  TextField, 
  Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { 
  useActualizarSensorMutation,
  useAgregarSensorMutation, 
  useBorrarSensorMutation, 
  useGetSensoresByCamaraIdQuery,  
} from '../../../api/frioApi';
import { ItemCamara, ItemFrigorifico, ItemPlanta, ItemSensor, SensorColor, SensorDato, SensorType } from '../interfaces/avanzado.interface';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { useNavigate } from 'react-router-dom';
import { handleMutationError } from '../../../api/handleMutationError';
import moment from 'moment';

type CardProps = {
    propsPlanta: ItemPlanta;
    propsFrio: ItemFrigorifico;
    propsCamara: ItemCamara;
}

export const AvanzadoSensorComponent: React.FC<CardProps> = ({propsPlanta, propsFrio, propsCamara}) => {
  
  const navigate = useNavigate();

  const { data = [], error, isLoading, refetch } = useGetSensoresByCamaraIdQuery(propsCamara.id);
  const [addItem, {isLoading: isLoadingAdd, isError: isErrorAdd, error: errorAdd}] = useAgregarSensorMutation();
  const [updateItem, {isLoading: isLoadingUpdate, isError: isErrorUpdate, error: errorUpdate}] = useActualizarSensorMutation();
  const [deleteItem, {isLoading: isLoadingDelete, isError: isErrorDelete, error: errorDelete}] = useBorrarSensorMutation();
  
  const [editItem, setEditItem] = useState<ItemSensor | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [openDialogDelete, setOpenDialogDelete] = useState(false);
  const [selectedIdDelete, setSelectedIdDelete] = useState<string | null>(null);

  if (error) {
    if (typeof error === 'object' && error !== null) {
      if('data' in error)
      {
        return (
          <section className="alert alert-danger">
          Falla de conexion con el servidor <div>{JSON.stringify(error.data)}</div>
          </section>
        );
      }
      return (
        <section className="alert alert-danger">
          Falla de conexion con el servidor <div>{JSON.stringify(error)}</div>
        </section>
      );
    }
  }

  const handleAddClick = () => {
    setEditItem(null);
    setIsDialogOpen(true);
  };

  const handleEditClick = (item: ItemSensor) => {
    setEditItem(item);
    setIsDialogOpen(true);
  };

  const handleDeleteClick = (id: string) => {
    setSelectedIdDelete(id);
    setOpenDialogDelete(true);
  };

  const confirmDelete = async () => {
    try {
      if (selectedIdDelete !== null) {
        await deleteItem(selectedIdDelete).unwrap();
        setOpenDialogDelete(false);
        setSelectedIdDelete(null);
        refetch();
      }
    } catch (err) {
      // Handle error, display message
      console.error("Error al elminar el sensor", err);
    }  
  };
  const cancelDelete = () => {
    setOpenDialogDelete(false);
    setSelectedIdDelete(null);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditItem(null);
  };

  const handleSaveClick = async (plantaId: string, frioId: string, camaraId: string) => {
    try{
      if (editItem) {
        if (editItem.id) {
          await updateItem(
            {
              id: editItem.id, 
              name_db: editItem.name_db,
              name_front: editItem.name_front,
              descripcion: editItem.descripcion,
              tipo_dato: editItem.tipo_dato,
              tipo_sensor: editItem.tipo_sensor,
              color_front: editItem.color_front,
              max_grafico: editItem.max_grafico,
              min_grafico: editItem.min_grafico,
              orden: editItem.orden,
              historico: editItem.historico,
              value: editItem.value,
              fecha_hora_value: editItem.fecha_hora_value,
              color_fondo: editItem.color_fondo,
              color_fuente: editItem.color_fuente,
              planta: plantaId,
              frigorifico: frioId,
              camara: camaraId
            }
          ).unwrap();
        } else {
          await addItem(
            {
              name_db: editItem.name_db,
              name_front: editItem.name_front,
              descripcion: editItem.descripcion,
              tipo_dato: editItem.tipo_dato === undefined? SensorDato.REAL_DIV_10 : editItem.tipo_dato,
              tipo_sensor: editItem.tipo_sensor === undefined? SensorType.TEMPERATURA : editItem.tipo_sensor,
              color_front: editItem.color_front === undefined? SensorColor.GREEN : editItem.color_front,
              max_grafico: editItem.max_grafico === undefined? 20 : editItem.max_grafico,
              min_grafico: editItem.min_grafico === undefined? -10 : editItem.min_grafico,
              orden: editItem.orden === undefined? 'A' : editItem.orden,
              historico: editItem.historico === undefined? false : editItem.historico,
              value: '0,0',
              color_fondo: '#FFFFFF', //Color blanco 
              color_fuente: '#000000', //Color negro              
              fecha_hora_value: moment().toDate(),//editItem.fecha_hora_value,    
              planta: plantaId,
              frigorifico: frioId,
              camara: camaraId
            }
          ).unwrap();
        }
        refetch();
        handleDialogClose();
      }
    } catch (err) {
      // Handle error, display message
      console.error("Error al guardar sensor", err);
    }
  }; 

  const handleViewPlantasClick = () => {
    navigate("/avanzado");
  };

  const handleViewFriosClick = (planta: ItemPlanta) => {
    navigate("/avanzado/frigorificos",{state: {planta}});
  };

  const handleViewCamarasClick = (planta: ItemPlanta, frio: ItemFrigorifico) => {  
    navigate("/avanzado/camaras",{state: {planta, frio}});
  };

  const columns: GridColDef[] = [
    { field: 'name_db', headerName: 'Nombre Db', flex: 1, minWidth: 150, editable: false },
    { field: 'name_front', headerName: 'Nombre Front', flex: 1, minWidth: 150, editable: false },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      flex: 0.5,
      minWidth: 120,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          color="primary"
          onClick={() => handleEditClick(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          color="error"
          onClick={() => handleDeleteClick(params.row.id as string)}
        />,
      ],
    },
    { field: 'historico', headerName: 'Historico', flex: 0.7, minWidth: 120, editable: false },
    { field: 'tipo_dato', headerName: 'Tipo Dato', flex: 0.7, minWidth: 120, editable: false },
    { field: 'tipo_sensor', headerName: 'Tipo Sensor', flex: 0.7, minWidth: 120, editable: false },
    { field: 'color_front', headerName: 'Color', flex: 0.5, minWidth: 100, editable: false },
    { field: 'max_grafico', headerName: 'Máximo Eje Y', flex: 0.3, minWidth: 60, editable: false },
    { field: 'min_grafico', headerName: 'Mínimo Eje y',  flex: 0.3, minWidth: 60, editable: false },
    { field: 'orden', headerName: 'Orden Aparicion',flex: 0.7, minWidth: 120, editable: false },
    /*{ field: 'value', headerName: 'Valor', width: 100, editable: false },
    { field: 'fecha_hora_value', headerName: 'Fecha y Hora Valor', width: 100, editable: false },*/
    {
        field: 'camara',
        headerName: 'Camara',
        flex: 0.7,
        minWidth: 100,
        // Accediendo al valor anidado dentro de `item`
        valueGetter: (params, row) => row.camara?.name ?? 'N/A',
    },
    { field: 'descripcion', headerName: 'Descripcion', flex: 0.7, minWidth: 120, editable: false },
    { field: 'id', headerName: 'ID',  flex: 0.3, minWidth: 60},

  ];

  //           



  return (
    <div>
    {isLoading ? (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    ) :
      <Box sx={{ height: 400, width: '100%', padding: 2 }}>
        <Typography variant="h5" component="h2" gutterBottom sx={{ textAlign: 'center' }}>
          Lista de sensores de la {`${propsCamara?.name}`} del frigorifico  {`${propsFrio?.name}`}
        </Typography>
          <Stack sx={{my: 2}} spacing={2} direction="row">
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleViewPlantasClick()}
            startIcon={<KeyboardDoubleArrowLeftIcon />} 
          >
            PLANTAS
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<KeyboardDoubleArrowLeftIcon />}  
            onClick={() => handleViewFriosClick(propsPlanta)}
          >
            FRIGORIFICOS
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<NavigateBeforeIcon />}  
            onClick={() => handleViewCamarasClick(propsPlanta, propsFrio)}
          >
            CAMARAS
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
          >
            AGREGAR SENSOR
          </Button>
        </Stack>
        <DataGrid
          rows={data!.data}
          columns={columns}
          editMode="row"
          onRowEditStop={() => {}}
        />
        
        <Dialog open={isDialogOpen} onClose={handleDialogClose}>
          <DialogTitle>{editItem ? 'Editar Sensor' : 'Agregar Sensor'}</DialogTitle>
          <DialogContent>
            {(isLoadingAdd || isLoadingUpdate) && <CircularProgress />}
            {isErrorAdd && <p>Error al guardar: {errorAdd? handleMutationError(errorAdd) : 'Falla no reconocida'}</p>}
            {isErrorUpdate && <p>Error al actualizar: {errorUpdate? handleMutationError(errorUpdate) : 'Falla no reconocida'}</p>}
            <TextField
              autoFocus
              margin="dense"
              label="Nombre db"
              fullWidth
              variant="standard"
              value={editItem?.name_db || ''}
              onChange={(e) => setEditItem({ ...editItem, name_db: e.target.value } as ItemSensor)}
            />
            <TextField
              autoFocus
              margin="dense"
              label="Nombre Front"
              fullWidth
              variant="standard"
              value={editItem?.name_front || ''}
              onChange={(e) => setEditItem({ ...editItem, name_front: e.target.value } as ItemSensor)}
            />
            <TextField
              autoFocus
              margin="dense"
              label="Descripcion"
              fullWidth
              variant="standard"
              value={editItem?.descripcion || ''}
              onChange={(e) => setEditItem({ ...editItem, descripcion: e.target.value } as ItemSensor)}
            />
            <FormControl variant="standard" fullWidth >
              <InputLabel>Tipo de Dato</InputLabel>
                <Select
                  value={editItem?.tipo_dato || SensorDato.REAL_DIV_10}
                  onChange={(e) => setEditItem({ ...editItem, tipo_dato: e.target.value } as ItemSensor)}
                  fullWidth
                  label="Tipo de Dato"
                  
                >
                  {Object.values(SensorDato).map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
            </FormControl>
            <FormControl variant="standard" fullWidth >
              <InputLabel>Tipo de Sensor</InputLabel>
                <Select
                  value={editItem?.tipo_sensor || SensorType.TEMPERATURA}
                  onChange={(e) => setEditItem({ ...editItem, tipo_sensor: e.target.value } as ItemSensor)}
                  fullWidth
                  label="Tipo de Sensor"
                  
                >
                  {Object.values(SensorType).map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
            </FormControl>
            <FormControl variant="standard" fullWidth >
              <InputLabel>Color</InputLabel>
                <Select
                  value={editItem?.color_front || SensorColor.GREEN}
                  onChange={(e) => setEditItem({ ...editItem, color_front: e.target.value } as ItemSensor)}
                  fullWidth
                  label="Color"
                >
                  {Object.values(SensorColor).map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
            </FormControl>
            <TextField
              autoFocus
              margin="dense"
              label="Maximo Eje Y"
              fullWidth
              variant="standard"
              type="number"
              //value={editItem?.max_grafico || ''}
              value={editItem?.max_grafico || 20}
              onChange={(e) => setEditItem({
                 ...editItem, 
                 max_grafico: e.target.value === '' ? undefined : parseInt(e.target.value)
                } as ItemSensor)}
            />
            <TextField
              autoFocus
              margin="dense"
              label="Mínimo Eje Y"
              fullWidth
              variant="standard"
              type="number"
              //value={editItem?.min_grafico || ''}
              value={editItem?.min_grafico || -10}
              onChange={(e) => setEditItem({
                 ...editItem, 
                 min_grafico: e.target.value === '' ? undefined : parseInt(e.target.value)
                } as ItemSensor)}
            />
            <TextField
              autoFocus
              margin="dense"
              label="Orden Aparición"
              fullWidth
              variant="standard"
              value={editItem?.orden || 'A'}
              onChange={(e) => setEditItem({ ...editItem, orden: e.target.value } as ItemSensor)}
            /> 
            <FormControl variant="standard" fullWidth >
              <InputLabel>Historico</InputLabel>
                <Select
                  value={editItem?.historico === true ? 1 : 0 }
                  onChange={(e) => setEditItem({ ...editItem, historico: Number(e.target.value) === 1} as ItemSensor)}
                  fullWidth
                  label="Historico"
                >
                <MenuItem value={1}>Sí</MenuItem>
                <MenuItem value={0}>No</MenuItem>
                </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} color="primary">
              Cancelar
            </Button>
            <Button onClick={() => handleSaveClick(propsPlanta.id as string, propsFrio.id as string, propsCamara.id as string)} color="primary">
              Guardar
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openDialogDelete} onClose={cancelDelete}>
          <DialogTitle>Confirmar eliminación</DialogTitle>
          <DialogContent>
            <Typography>
              ¿Está seguro de que desea eliminar el sensor?
            </Typography>
          </DialogContent>
          <DialogActions>
            {isLoadingDelete && <CircularProgress />}
            {isErrorDelete && <p>Error al borrar: {errorDelete? handleMutationError(errorDelete) : 'Falla no reconocida'}</p>}
            <Button onClick={cancelDelete} color="primary">
              Cancelar
            </Button>
            <Button onClick={confirmDelete} color="error">
              Eliminar
            </Button>
          </DialogActions>
        </Dialog>

    </Box>
    }
    </div>
  );
};
